import {cdnImage, cdnUrl} from '@/util/cdnImage';
import {AWARD_CARDS} from '@/constants/badges';
import type {MvpType} from '@/components/Ui-components/mvp-development/MvpTypes';
import type {HoverRevealCard} from '@/components/Ui-components/CommonSections/HoverRevealCards';

// The React Native page's own artwork, on the CDN under `images/React-native-app-images/`.
// Names match the uploaded files exactly — including the few with spaces or odd prefixes.
const img = (name: string) => cdnUrl(`React-native-app-images/${name}`);

export const rnHeroImage = img('react-native-banner-2.webp');

export const rnServicesImage = img('react-native-development-services.png');
// "Proven Expertise. Measurable Results." — built-in icon keys from MvpStatsSection.
export const rnStats = [
    {icon: 'cube', value: '500+', label: 'Solutions Delivered'},
    {icon: 'people', value: '150+', label: 'Engineers & Technology Specialists'},
    {icon: 'trend', value: '$62M+', label: 'Raised By Client Platforms'},
    {icon: 'globe', value: '25+', label: 'Industries Served Globally'},
    {icon: 'star', value: '15+', label: 'Years of Engineering Experience'}
];

export const rnFeaturesImage = img('react-native-app-features.webp');

export const rnServicesImageCaption = 'Consulting → Design → Engineering → Launch → Evolution';

export const rnServices = [
    {
        title: 'React Native Consulting',
        description:
            'Shape the right product and engineering direction before development starts. We help define scope, assess React Native suitability, plan the architecture, and surface delivery risks early.'
    },
    {
        title: 'React Native UI/UX Development',
        description:
            'Design mobile experiences that feel intuitive across iOS and Android. We translate product requirements into responsive interfaces that support usability, accessibility, and consistent brand presentation.'
    },
    {
        title: 'Custom React Native App Development',
        description:
            'Develop purpose-built React Native applications for customer, workforce, and enterprise use cases. We align the application with your workflows, backend systems, user needs, and future roadmap.'
    },
    {
        title: 'React Native Migration',
        description:
            'Modernize an existing mobile product by moving suitable functionality to React Native. We plan the transition carefully to reduce disruption and preserve critical user and business workflows.'
    },
    {
        title: 'API & Enterprise Integration',
        description:
            'Make your React Native application work with the wider technology environment around it. We connect mobile workflows with business platforms, backend services, cloud systems, payments, identity, and external APIs.'
    },
    {
        title: 'React Native Testing & Performance Optimization',
        description:
            'Strengthen application quality before and after release. We validate core workflows, device behavior, integrations, stability, and performance across supported iOS and Android environments.'
    },
    {
        title: 'React Native MVP Development',
        description:
            'Turn a validated product concept into a production-ready first release. We focus development on the functionality needed to test adoption while keeping the foundation ready for expansion.'
    },
    {
        title: 'React Native Maintenance & Product Evolution',
        description:
            'Keep the application current as platforms, dependencies, and product priorities change. We support upgrades, issue resolution, performance improvements, security updates, and planned feature releases.'
    },
    {
        title: 'Native Module Development',
        description:
            'Extend React Native when deeper platform access is required. We develop native iOS and Android modules for specialized SDKs, device capabilities, background processes, and platform-specific functionality.'
    }
];

export const rnFitCta = {
    heading: 'Is React Native the Right Fit for Your Mobile Product?',
    subheading:
        'React Native is a strong fit when you need efficient iOS and Android delivery without giving up access to native capabilities.',
    bullets: [
        'You want one product experience across iOS and Android.',
        'You want to reuse business logic and UI components across platforms.',
        'Your product relies on APIs, payments, analytics, notifications, or third-party SDKs.',
        'You need selected native functionality without building two separate apps.'
    ],
    buttonText: 'Get Your Product Assessment Report',
    backgroundImage: img('react-native-app-development-cta-bg.webp')
};

export const rnAppTypes: MvpType[] = [
    {
        id: 'enterprise',
        title: 'Enterprise Mobile Applications',
        points: ['ERP and CRM extensions', 'Approval and operational workflows', 'Role-based access', 'Secure enterprise integrations'],
        image: img('enterprise-mobile-applications.webp')
    },
    {
        id: 'saas',
        title: 'SaaS Mobile Applications',
        points: ['Multi-tenant functionality', 'User dashboards', 'Subscription management', 'API-based integrations'],
        image: img('saas-mobile-applications.webp')
    },
    {
        id: 'ecommerce',
        title: 'E-commerce Applications',
        points: ['Product catalogs and search', 'Cart and checkout', 'Payment integrations', 'Order tracking'],
        image: img('ecommerce-applications.webp')
    },
    {
        id: 'marketplace',
        title: 'Marketplace Applications',
        points: ['Buyer and seller onboarding', 'Search and matching', 'Payments and commissions', 'Ratings and reviews'],
        image: img('marketplace-applications.webp')
    },
    {
        id: 'on-demand',
        title: 'On-Demand Service Applications',
        points: ['Booking and scheduling', 'Location tracking', 'Provider management', 'Real-time status updates'],
        image: img('on-demand-service-applications.webp')
    },
    {
        id: 'customer-portals',
        title: 'Customer Portals',
        points: ['Account management', 'Self-service features', 'Document access', 'Notifications and support'],
        image: img('customer-portals.webp')
    },
    {
        id: 'workforce',
        title: 'Employee & Workforce Applications',
        points: ['Task management', 'Internal communication', 'Field data collection', 'Attendance and workforce workflows'],
        image: img('employee-workforce-applications.webp')
    },
    {
        id: 'field-service',
        title: 'Field Service Applications',
        points: ['Work orders', 'Inspection workflows', 'Offline data capture', 'Location and asset tracking'],
        image: img('field-service-applications.webp')
    },
    {
        id: 'booking',
        title: 'Booking & Reservation Applications',
        points: ['Real-time availability', 'Scheduling', 'Payments', 'Booking management'],
        image: img('booking-reservation-applications.webp')
    },
    {
        id: 'streaming',
        title: 'Content & Streaming Applications',
        points: ['Content libraries', 'Audio and video streaming', 'Subscriptions', 'Personalized recommendations'],
        image: img('content-streaming-applications.webp')
    },
    {
        id: 'communication',
        title: 'Communication & Collaboration Applications',
        points: ['Real-time messaging', 'Group communication', 'File sharing', 'Push notifications'],
        image: img('communication-collaboration-applications.webp')
    },
    {
        id: 'dashboards',
        title: 'Data & Dashboard Applications',
        points: ['Interactive dashboards', 'Reporting', 'Real-time alerts', 'Data visualization'],
        image: img('data-dashboard-applications.webp')
    }
];

// Feature → business outcome. Rendered by `WhyBuildMvp`, which has no tag row to fill here.
export const rnFeatures = [
    {
        title: 'Shared iOS & Android Codebase',
        description:
            'Reuse significant application logic across both platforms while maintaining platform-specific behavior where required.',
        icon: img('shared-ios-android-codebase.svg'),
        tags: []
    },
    {
        title: 'Native UI Components',
        description: 'Build interfaces using native platform elements for familiar iOS and Android interactions.',
        icon: img('native-ui-components.svg'),
        tags: []
    },
    {
        title: 'Native Device Access',
        description: 'Support camera, GPS, biometrics, Bluetooth, notifications, secure storage, and other device capabilities.',
        icon: img('native-device-access.svg'),
        tags: []
    },
    {
        title: 'Native Module Extensibility',
        description: 'Add Swift or Kotlin modules when specialized SDKs or platform-specific functionality require native implementation.',
        icon: img('native-module-extensibility.svg'),
        tags: []
    },
    {
        title: 'Hermes JavaScript Engine',
        description: 'Improve application startup, runtime efficiency, and memory behavior in supported React Native applications.',
        icon: img('hermes-javascript-engine.svg'),
        tags: []
    },
    {
        title: 'New Architecture & JSI',
        description: 'Enable more efficient interaction between JavaScript and native functionality for demanding application workflows.',
        icon: img('new-architecture-jsi.svg'),
        tags: []
    },
    {
        title: 'Reusable Component Architecture',
        description: 'Create shared UI and business components that can be maintained and extended across product releases.',
        icon: img('reusable-component-architecture.svg'),
        tags: []
    },
    {
        title: 'Responsive Cross-Device Layouts',
        description: 'Adapt interfaces across different screen sizes and device orientations using React Native layout capabilities.',
        icon: img('responsive-cross-device-layouts.svg'),
        tags: []
    },
    {
        title: 'Accessibility Support',
        description: 'Build mobile experiences that work with native accessibility technologies such as VoiceOver and TalkBack.',
        icon: img('accessibility-support.svg'),
        tags: []
    },
    {
        title: 'Fast Refresh',
        description: 'Help development teams validate interface and code changes quickly during implementation.',
        icon: img('fast-refresh.svg'),
        tags: []
    },
    {
        title: 'TypeScript Support',
        description: 'Improve code maintainability and reliability for larger React Native codebases.',
        icon: img('typescript-support.svg'),
        tags: []
    },
    {
        title: 'Expo Ecosystem Support',
        description: 'Use Expo tooling where appropriate for routing, builds, deployment workflows, and supported device APIs.',
        icon: img('expo-ecosystem-support.svg'),
        tags: []
    }
];

// Industry photography is shared with the home, MVP and iOS pages.
export const rnIndustries = [
    {
        title: 'Financial Services',
        desc: 'From digital banking to investment platforms, our <strong>fintech app development expertise</strong> helps institutions deliver secure transactions, simplify account management, and connect mobile experiences with existing financial infrastructure.',
        image: cdnUrl('home-page-updated-assets/fintech-img.webp'),
        imageAlt: 'Person making a secure card payment on a laptop with digital banking icons overlaid',
        services: [
            {heading: 'Digital Wallets & Payments'},
            {heading: 'Account Onboarding & KYC'},
            {heading: 'Trading & Portfolio Management'},
            {heading: 'Personal Banking Applications'},
            {heading: 'Biometric Authentication'}
        ]
    },
    {
        title: 'Healthcare',
        desc: 'We develop <strong>custom healthcare mobile applications</strong> that connect patients, providers, and clinical systems. These solutions support virtual consultations, remote monitoring, and coordinated care while addressing healthcare privacy requirements.',
        image: cdnUrl('home-page-updated-assets/heathcare-img.webp'),
        imageAlt: 'Doctor reviewing patient vitals on a tablet beside a hospital monitoring display',
        services: [
            {heading: 'Patient Portals'},
            {heading: 'Appointment Management'},
            {heading: 'Telehealth Applications'},
            {heading: 'Remote Patient Monitoring'},
            {heading: 'Healthcare System Integration'}
        ]
    },
    {
        title: 'Logistics & Supply Chain',
        desc: 'Our logistics app development services help businesses manage fleet operations, track shipments, coordinate warehouse activities, and improve supply chain visibility through real-time data access and offline capabilities.',
        image: cdnUrl('home-page-updated-assets/logistics-supply-chain-img.webp'),
        imageAlt: 'Logistics operations with shipment tracking across a supply chain',
        services: [
            {heading: 'Fleet & Shipment Tracking'},
            {heading: 'Proof of Delivery'},
            {heading: 'Warehouse Management'},
            {heading: 'Route Optimization'},
            {heading: 'Offline Mobile Applications'}
        ]
    },
    {
        title: 'Retail & eCommerce',
        desc: 'Our eCommerce app development services help retailers create connected shopping experiences, simplify mobile transactions, strengthen customer loyalty, and manage orders across multiple sales and fulfillment channels.',
        image: cdnUrl('mvp-asset/retail-ecommerce.webp'),
        imageAlt: 'Shopper browsing products on a mobile commerce app',
        services: [
            {heading: 'Product Catalogs'},
            {heading: 'Shopping Applications'},
            {heading: 'Loyalty Programs'},
            {heading: 'Payment Experiences'},
            {heading: 'Order Tracking'}
        ]
    },
    {
        title: 'Enterprise & SaaS',
        desc: 'Extend existing business platforms through enterprise mobile application development that brings critical workflows, customer data, and operational dashboards to employees and users without disrupting core systems.',
        image: cdnUrl('mvp-asset/saas-enterprise-software.webp'),
        imageAlt: 'Business team reviewing SaaS dashboards and enterprise workflows',
        services: [
            {heading: 'Employee Applications'},
            {heading: 'Customer Portals'},
            {heading: 'Business Dashboards'},
            {heading: 'Workflow Automation'},
            {heading: 'Enterprise Integrations'}
        ]
    },
    {
        title: 'Real Estate',
        desc: 'Simplify property discovery and management with real estate mobile solutions designed for buyers, agents, and property managers. These applications bring listings, virtual tours, scheduling, and tenant services into one accessible experience.',
        image: cdnUrl('home-page-updated-assets/real-estate-img.webp'),
        imageAlt: 'Property listing and real estate transaction on a digital platform',
        services: [
            {heading: 'Property Search Applications'},
            {heading: 'Virtual Tours'},
            {heading: 'Agent Management'},
            {heading: 'Property Listings'},
            {heading: 'Tenant Portals'}
        ]
    }
];

export const rnAiColumns = ['Client Need', 'What They Get', 'Business Benefit'];

export const rnAiRows = [
    {
        criteria: 'AI Assistants & Chatbots',
        cells: [
            'Users need faster access to answers, services, or support inside the app',
            'In-app AI assistants connected to approved business data and workflows',
            'Faster response times, improved self-service, and reduced support effort'
        ]
    },
    {
        criteria: 'AI-Powered Search',
        cells: [
            'Users struggle to find relevant products, content, records, or information',
            'Intelligent search with contextual and semantic discovery',
            'Faster information access and better user engagement'
        ]
    },
    {
        criteria: 'Recommendation Engines',
        cells: [
            'Users need more relevant content, products, or next-best actions',
            'Personalized recommendations based on user behavior and application data',
            'Higher engagement, stronger conversion, and improved retention'
        ]
    },
    {
        criteria: 'Document Intelligence',
        cells: [
            'Mobile workflows involve forms, documents, images, or unstructured data',
            'AI-assisted extraction, classification, summarization, and data capture',
            'Reduced manual processing and faster operational workflows'
        ]
    },
    {
        criteria: 'AI Workflow Automation',
        cells: [
            'Repetitive mobile processes require manual routing, follow-ups, or decisions',
            'AI-supported automation for task routing, approvals, notifications, and next actions',
            'Lower operational effort and shorter turnaround times'
        ]
    },
    {
        criteria: 'Predictive Insights',
        cells: [
            'Users need earlier visibility into trends, risks, or likely outcomes',
            'AI-driven predictions and contextual insights within mobile workflows',
            'Better decisions and more proactive business operations'
        ]
    }
];

// Paragraphs are parsed as HTML; the second carries each phase's deliverables.
const deliverables = (items: string[]) => `<strong class="font-headers">Deliverables:</strong> ${items.join(' · ')}`;

export const rnProcessSteps = [
    {
        title: 'Discovery & Product Requirements',
        paragraphs: [
            'Define users, workflows, platforms, business goals, scope boundaries, integrations, security needs, and measurable product outcomes.',
            deliverables(['Business requirements', 'User and workflow analysis', 'Initial scope', 'Success criteria'])
        ]
    },
    {
        title: 'Architecture & Product Planning',
        paragraphs: [
            'Define the <a href="/blog/react-native-architecture-a-complete-overview" target="_blank">React Native architecture</a>, shared-code boundaries, native module requirements, API strategy, environments, release plan, and delivery milestones.',
            deliverables(['Architecture plan', 'Integration map', 'Release roadmap', 'Risk assessment'])
        ]
    },
    {
        title: 'UI/UX Design',
        paragraphs: [
            'Create user flows, prototypes, reusable interface components, mobile interaction states, accessibility patterns, and platform-aware layouts.',
            deliverables(['User flows', 'Prototype', 'Design system', 'Component specifications'])
        ]
    },
    {
        title: 'React Native Engineering',
        paragraphs: [
            'Build reusable components, TypeScript business logic, application state, navigation, local storage, analytics, and native modules where required.',
            deliverables(['Application modules', 'Native bridges', 'Automated tests', 'Development builds'])
        ]
    },
    {
        title: 'API & Third-Party Integration',
        paragraphs: [
            'Connect the application to backend services, REST and third-party APIs, identity, payment, cloud, and business platforms.',
            deliverables(['API integrations', 'Authentication', 'Third-party SDKs', 'Environment configuration'])
        ]
    },
    {
        title: 'Testing, QA & Performance Validation',
        paragraphs: [
            'Validate core workflows, device compatibility, integrations, operating systems, offline behavior, crashes, performance, and security-sensitive paths.',
            deliverables(['QA report', 'Regression coverage', 'Performance findings', 'Release candidate'])
        ]
    },
    {
        title: 'App Store & Google Play Deployment',
        paragraphs: [
            'Prepare production builds, signing, testing tracks, release configuration, store submission, monitoring, and staged rollout support.',
            deliverables(['Production builds', 'Store submission', 'Release notes', 'Deployment plan'])
        ]
    },
    {
        title: 'Maintenance & Product Evolution',
        paragraphs: [
            'Support production issues, framework upgrades, dependency changes, new OS versions, performance improvements, and roadmap releases.',
            deliverables(['Monitoring', 'Upgrade planning', 'Issue resolution', 'Roadmap delivery'])
        ]
    }
];

// Logos are existing CDN artwork plus this page's own icons. Items without one (no logo on
// the CDN yet) render as a text-only chip.

const tech = {
    reactNative: cdnImage('hire-remote-developer/React-Native.webp'),
    typescript: cdnImage('hire-remote-developer/TypeScript.webp'),
    javascript: cdnImage('hire-remote-developer/JavaScript.webp'),
    react: cdnImage('Hire-team/React-js.webp'),
    expo: cdnImage('Hire-React-Native/Expo.webp'),
    reactNavigation: cdnImage('Hire-React-Native/React-Navigation.webp'),
    styledComponents: cdnImage('hire-reactJs/Styled-Components.webp'),
    redux: cdnImage('reactnative/Redux.webp'),
    zustand: cdnImage('react-images/Zustand.webp'),
    mobx: cdnImage('reactnative/MobX.webp'),
    swift: cdnUrl('mvp-asset/swift.svg'),
    objectiveC: cdnImage('ios/Objective-C.webp'),
    kotlin: cdnImage('hire-remote-developer/Kotlin.webp'),
    java: cdnImage('hire-remote-developer/Java.webp'),
    node: cdnImage('hire-remote-developer/node-js.webp'),
    python: cdnImage('hire-remote-developer/Python.webp'),
    dotnet: cdnImage('Hire-dot-net-developer/Dot-NET-Core.webp'),
    rest: cdnImage('ai-copliot/REST.webp'),
    graphql: cdnImage('ai-copliot/GraphQL.webp'),
    websockets: cdnImage('crypto-exchange-revamp-file/websocket.webp'),
    sqlite: cdnImage('Hire-web-developers/SQLite.webp'),
    realm: cdnImage('ios/realm.webp'),
    firestore: cdnImage('mobile-app-new-images/Cloud-Firestore.webp'),
    postgres: cdnImage('Hire-team/PostgreSQL.webp'),
    mongodb: cdnImage('Hire-team/MongoDB.webp'),
    aws: cdnImage('ai/aws.webp'),
    azure: cdnImage('hire-remote-developer/Microsoft-Azure.webp'),
    googleCloud: cdnImage('Tech-stack-icons/google-cloud.webp'),
    firebase: cdnImage('hybrid-crypto-exchange/Firebase.webp'),
    docker: cdnImage('hire-remote-developer/docker.webp'),
    oauth: cdnImage('ai-copliot/OAuth-2.webp'),
    openId: cdnUrl('ios-app-development/openid-connect.svg'),
    auth0: cdnImage('Cross-Platform-App-Development/Auth0.webp'),
    jest: cdnImage('Token-development-company/Jest.webp'),
    detox: cdnImage('Hire-mobile-app-developers/Detox.webp'),
    appium: cdnImage('hire-remote-developer/Appium.webp'),
    sentry: cdnImage('Hire-React-Native/Sentry.webp'),
    crashlytics: cdnImage('ios/Crashlytics.webp'),
    githubActions: cdnImage('Tech-stack-icons/github-actions.webp'),
    gitlab: cdnImage('generative-ai-integration/GitLab.webp'),
    fastlane: cdnImage('reactnative/Fastlane.webp'),
    hermes: img('hermes.svg'),
    reanimated: img('react-native-reanimated.svg'),
    nativewind: img('nativewind.svg'),
    turboModules: img('react-native-turbomodules.svg'),
    asyncStorage: img('react-native-async-storage.svg'),
    mmkv: img('mmkv.svg'),
    appleSignIn: img('sign-in-with-apple.svg'),
    rnTestingLibrary: img('react-native-testing-library.svg')
};

export const rnTechCategories = [
    {
        category: 'Core React Native Development',
        items: [
            {label: 'React Native', icon: tech.reactNative},
            {label: 'TypeScript', icon: tech.typescript},
            {label: 'JavaScript', icon: tech.javascript},
            {label: 'React', icon: tech.react},
            {label: 'React Native CLI', icon: tech.reactNative},
            {label: 'Expo', icon: tech.expo},
            {label: 'Hermes', icon: tech.hermes}
        ]
    },
    {
        category: 'UI, Navigation & Interaction',
        items: [
            {label: 'React Navigation', icon: tech.reactNavigation},
            {label: 'Expo Router', icon: tech.expo},
            {label: 'React Native Reanimated', icon: tech.reanimated},
            {label: 'React Native Gesture Handler'},
            {label: 'NativeWind', icon: tech.nativewind},
            {label: 'Styled Components', icon: tech.styledComponents}
        ]
    },
    {
        category: 'State Management & Data',
        items: [
            {label: 'Redux Toolkit', icon: tech.redux},
            {label: 'Zustand', icon: tech.zustand},
            {label: 'TanStack Query'},
            {label: 'Context API', icon: tech.react},
            {label: 'MobX', icon: tech.mobx}
        ]
    },
    {
        category: 'Native iOS & Android Development',
        items: [
            {label: 'Swift', icon: tech.swift},
            {label: 'Objective-C', icon: tech.objectiveC},
            {label: 'Kotlin', icon: tech.kotlin},
            {label: 'Java', icon: tech.java},
            {label: 'Native Modules', icon: tech.reactNative},
            {label: 'TurboModules', icon: tech.turboModules}
        ]
    },
    {
        category: 'Backend & APIs',
        items: [
            {label: 'Node.js', icon: tech.node},
            {label: 'Python', icon: tech.python},
            {label: '.NET', icon: tech.dotnet},
            {label: 'Java', icon: tech.java},
            {label: 'REST APIs', icon: tech.rest},
            {label: 'GraphQL', icon: tech.graphql},
            {label: 'WebSockets', icon: tech.websockets}
        ]
    },
    {
        category: 'Databases & Offline Storage',
        items: [
            {label: 'SQLite', icon: tech.sqlite},
            {label: 'Realm', icon: tech.realm},
            {label: 'Firebase Firestore', icon: tech.firestore},
            {label: 'AsyncStorage', icon: tech.asyncStorage},
            {label: 'MMKV', icon: tech.mmkv},
            {label: 'PostgreSQL', icon: tech.postgres},
            {label: 'MongoDB', icon: tech.mongodb}
        ]
    },
    {
        category: 'Cloud & Infrastructure',
        items: [
            {label: 'AWS', icon: tech.aws},
            {label: 'Microsoft Azure', icon: tech.azure},
            {label: 'Google Cloud', icon: tech.googleCloud},
            {label: 'Firebase', icon: tech.firebase},
            {label: 'Docker', icon: tech.docker}
        ]
    },
    {
        category: 'Authentication & Security',
        items: [
            {label: 'OAuth 2.0', icon: tech.oauth},
            {label: 'OpenID Connect', icon: tech.openId},
            {label: 'Auth0', icon: tech.auth0},
            {label: 'Firebase Authentication', icon: tech.firebase},
            {label: 'Apple Sign In', icon: tech.appleSignIn},
            {label: 'Google Sign-In'},
            {label: 'Biometric Authentication'}
        ]
    },
    {
        category: 'Testing & Monitoring',
        items: [
            {label: 'Jest', icon: tech.jest},
            {label: 'React Native Testing Library', icon: tech.rnTestingLibrary},
            {label: 'Detox', icon: tech.detox},
            {label: 'Appium', icon: tech.appium},
            {label: 'Sentry', icon: tech.sentry},
            {label: 'Firebase Crashlytics', icon: tech.crashlytics}
        ]
    },
    {
        category: 'CI/CD & Release Management',
        items: [
            {label: 'GitHub Actions', icon: tech.githubActions},
            {label: 'GitLab CI/CD', icon: tech.gitlab},
            {label: 'Fastlane', icon: tech.fastlane},
            {label: 'Firebase App Distribution', icon: tech.firebase},
            {label: 'App Store Connect', icon: cdnUrl('ios-app-development/App-Store-Connect-Analytics.svg')},
            {label: 'Google Play Console'}
        ]
    }
];

export const rnIntegrations = [
    {title: 'Identity & Access', points: ['SSO', 'OAuth / OIDC', 'Firebase Authentication', 'Auth0', 'Enterprise identity', 'Biometrics']},
    {title: 'Payments & Commerce', points: ['Stripe', 'Payment gateways', 'In-app purchases', 'Subscriptions', 'Transaction services']},
    {
        title: 'Enterprise Platforms',
        points: ['CRM', 'ERP', 'Customer portals', 'Internal APIs', 'Document systems', 'Workflow platforms']
    },
    {title: 'Communication', points: ['Push notifications', 'SMS', 'Email', 'In-app messaging', 'Customer support systems']},
    {title: 'Maps & Location', points: ['Google Maps', 'Mapbox', 'Geolocation', 'Routing', 'Geofencing', 'Real-time tracking']},
    {title: 'Analytics & Intelligence', points: ['Firebase Analytics', 'Product analytics', 'Crash reporting', 'Events', 'BI feeds']},
    {
        title: 'Cloud & Data Services',
        points: ['AWS', 'Azure', 'Google Cloud', 'Firebase', 'Cloud storage', 'Serverless', 'Real-time data']
    },
    {
        title: 'AI & Intelligent Services',
        href: '/ai-development-company',
        points: ['AI assistants', 'Recommendations', 'Semantic search', 'Document intelligence', 'Workflow automation']
    }
];

// Badges come from this page's artwork or the shared compliance set. NIST CSF and Singapore
// PDPA have no official mark on the CDN, so they render the section's neutral fallback tile.
const badge = (name: string) => cdnUrl(`home-page-updated-assets/${name}`);

export const rnComplianceGroups = [
    {
        title: 'Privacy & Data Protection',
        badges: [
            {name: 'GDPR', icon: badge('gdpr-compliance.svg')},
            {name: 'UK GDPR & Data Protection Act 2018', icon: img('uk-gdpr-data-protection-act-2018.svg')},
            {name: 'CCPA', icon: badge('ccpa-compliance.svg')},
            {name: 'CPRA', icon: img('cpra.svg')},
            {name: 'PIPEDA', icon: badge('pipeda-compliance.svg')},
            {name: 'PHIPA', icon: img('phipa.svg')},
            {name: 'LGPD', icon: img('lgpd.svg')},
            {name: 'UAE PDPL', icon: img('uae-pdpl.svg')},
            {name: 'Saudi PDPL', icon: img('saudi-pdpl.svg')},
            {name: 'Singapore PDPA'}
        ]
    },
    {
        title: 'Security & Assurance',
        badges: [
            {name: 'ISO/IEC 27001', icon: badge('iso-27001-compliance.svg')},
            {name: 'ISO/IEC 27701', icon: img('iso-iec-27701.svg')},
            {name: 'SOC 2', icon: badge('soc2-compliance.svg')},
            {name: 'NIST Cybersecurity Framework'},
            {name: 'OWASP MASVS', icon: img('owasp-masvs.svg')},
            {name: 'CIS Controls', icon: img('cis-controls.svg')},
            {name: 'ACSC Essential Eight', icon: img('acsc-essential-eight.svg')}
        ]
    },
    {
        title: 'Healthcare & Regulated Data',
        badges: [
            {name: 'HIPAA', icon: badge('hipaa-compliance.svg')},
            {name: 'HITECH', icon: img('hitech.svg')},
            {name: 'HITRUST CSF', icon: img('hitrust-csf.svg')}
        ]
    },
    {
        title: 'Payments & Financial Services',
        badges: [
            {name: 'PCI DSS', icon: badge('pci-dss-compliance.svg')},
            {name: 'GLBA', icon: img('glba.svg')},
            {name: 'DORA', icon: img('dora.svg')},
            {name: 'PSD2', icon: badge('psd2-compliance.svg')}
        ]
    }
];

export const rnWhyChooseCards: HoverRevealCard[] = [
    {
        title: 'Product-First Engineering Approach',
        description:
            'We align architecture, user journeys, integrations, and delivery priorities with measurable business goals before development begins.',
        image: img('product-first-engineering-approach.png')
    },
    {
        title: 'Cross-Platform and Native Expertise',
        description:
            'Our <a href="/hire-react-native-developers" target="_blank"><strong>React Native app developers</strong></a> combine shared architecture with Swift and Kotlin capabilities for platform-specific requirements.',
        image: img('cross-platform-native-expertise.png')
    },
    {
        title: 'End-to-End Development Ownership',
        description:
            'Our <strong>React Native app development services</strong> cover discovery, UX, engineering, testing, deployment, modernization, and ongoing product evolution.',
        image: img('end-to-end-development-ownership.png')
    },
    {
        title: 'Enterprise Integration Capabilities',
        description:
            'We connect mobile applications with CRM, ERP, cloud platforms, identity systems, payment services, and existing backend infrastructure.',
        image: img('Enterprise Integration Capabilities.png')
    }
];

export const rnChallenges = [
    {
        label: 'Platform Duplication',
        title: 'Duplicated iOS and Android Development',
        description: 'Separate codebases increase engineering effort and make maintaining feature parity across platforms harder.'
    },
    {
        label: 'Release Velocity',
        title: 'Long Mobile Release Cycles',
        description: 'Separate development and testing pipelines delay releases and slow product improvements across iOS and Android.'
    },
    {
        label: 'Modernization',
        title: 'Legacy Mobile Applications',
        description:
            'Aging mobile architectures increase technical debt, raise maintenance effort, and make future upgrades more difficult.'
    },
    {
        label: 'Enterprise Connectivity',
        title: 'Disconnected Enterprise Workflows',
        description: 'Limited mobile access to business systems creates manual handoffs and slows critical operational workflows.'
    },
    {
        label: 'Native Capabilities',
        title: 'Platform-Specific Feature Complexity',
        description:
            'Device-specific functionality can create performance, integration, and architectural challenges when poorly implemented.'
    },
    {
        label: 'Maintenance Efficiency',
        title: 'Growing Mobile Maintenance Costs',
        description: 'Repeated updates, testing, and defect resolution consume engineering capacity that could support product development.'
    }
];

// The MVP page's cost artwork is generic (no MVP branding in the image), so it is reused here,
// as the iOS page does. Timelines come from the FAQ's timeline table: "Production Application"
// is taken to be the Growth-Stage tier.
export const rnCostHighlight = {
    range: '$20K–$150K+',
    caption: 'React Native development typically ranges from $20,000 to $150,000+, depending on the product scope and complexity.',
    image: cdnUrl('mvp-asset/MVP-Development-Cost-&-Timeline .webp')
};

export const rnCostEstimator = {
    title: 'Cost by Product Scope',
    description: 'These ranges are indicative. Final pricing is based on validated requirements, technical scope, and delivery model.',
    scenarios: [
        {
            id: 'mvp',
            label: 'Focused MVP',
            cost: '$20,000–$35,000',
            timeline: '10–12 Weeks',
            scope: 'Core workflows, authentication, standard APIs, and limited integrations'
        },
        {
            id: 'growth',
            label: 'Growth-Stage Product',
            cost: '$35,000–$70,000',
            timeline: '4–7 Months',
            scope: 'Multiple user roles, payments, analytics, notifications, and broader integrations'
        },
        {
            id: 'enterprise',
            label: 'Enterprise Application',
            cost: '$70,000–$150,000+',
            timeline: '7–12+ Months',
            scope: 'Enterprise systems, SSO, offline workflows, advanced security, and custom native functionality'
        }
    ]
};

export const rnFinalCta = {
    heading: 'Need a React Native Development Company for Your Next Mobile Product?',
    subheading:
        'Share your target platforms, product scope, integrations, native functionality, security requirements, and delivery priorities. We can help define the architecture, team, timeline, and practical next step.',
    buttonText: 'Discuss Your React Native Project',
    image: img('react-native-development-company-cta-img.webp'),
    imageWidth: 671,
    imageHeight: 687,
    imageAlt: 'Hand holding a smartphone running a React Native app connected to code, data, cloud, and security services'
};

// Answers are parsed as HTML inside a <p>, so lists are written as prose (no <ul>/<table>).
export const rnFaqData = [
    {
        category: 'Cost & Timeline',
        questions: [
            {
                q: 'How much does React Native app development cost?',
                a: '<a href="/blog/react-native-app-development-cost" target="_blank">React Native app development cost</a> typically ranges from <strong>$20,000 to $150,000+</strong>. A focused MVP may cost between <strong>$20,000 and $35,000</strong>, while a growth-stage product can cost between <strong>$35,000 and $70,000</strong>. Enterprise applications with advanced integrations, offline workflows, security controls, or custom native modules may exceed <strong>$70,000 to $150,000+</strong>. Final pricing depends on architecture, integrations, native requirements, QA scope, and delivery complexity.'
            },
            {
                q: 'How long does React Native app development take?',
                a: 'A focused MVP typically takes <strong>10–12 weeks</strong>, a production application <strong>4–7 months</strong>, and an enterprise application <strong>7–12+ months</strong>. Backend readiness, third-party integrations, native features, security requirements, and testing depth can significantly affect the final schedule.'
            }
        ]
    },
    {
        category: 'React Native',
        questions: [
            {
                q: 'Why choose React Native over native development?',
                a: 'React Native makes sense when iOS and Android share most product logic and workflows. It can reduce duplicated engineering, simplify feature parity, and support coordinated releases across both platforms. Native development remains preferable when the application depends heavily on platform-specific processing, specialized hardware, or deeply customized native experiences. The decision should follow product economics and architecture rather than framework popularity.'
            },
            {
                q: 'Is React Native suitable for enterprise mobile applications?',
                a: 'Yes. React Native can support complex enterprise products when the surrounding architecture is designed correctly. Typical enterprise use cases include workforce applications, CRM and ERP extensions, customer portals, field service applications, SaaS mobile products, and operational dashboards. Enterprise implementations can also support SSO, role-based access, offline workflows, secure APIs, audit logging, and compliance-aligned controls.'
            },
            {
                q: 'Can React Native access native iOS and Android features?',
                a: 'Yes. React Native can access native functionality through platform APIs, SDKs, and custom native modules. Examples include camera access, GPS, biometrics, Bluetooth, push notifications, background processing, secure storage, and proprietary device SDKs. Where needed, <strong>Swift or Objective-C</strong> can support iOS-specific functionality, while <strong>Kotlin or Java</strong> can handle Android-specific requirements.'
            },
            {
                q: 'Can you migrate an existing native or legacy app to React Native?',
                a: 'Yes, but the migration strategy depends on the existing codebase. We usually evaluate <strong>Architecture → Native Dependencies → Integrations → Feature Parity → Technical Debt → Release Risk</strong>. Based on that assessment, migration can be phased, modular, or handled as a broader re-platforming initiative. For many established products, phased migration reduces operational risk and avoids a disruptive full rewrite.'
            },
            {
                q: 'Can React Native support offline mobile apps?',
                a: 'Yes. React Native can support applications that need to function with limited or unavailable connectivity. A reliable offline architecture usually combines local storage, queued actions, background synchronization, retry logic, and conflict resolution. This is particularly relevant for field service, logistics, healthcare, inspection, and distributed workforce applications where connectivity cannot be assumed.'
            },
            {
                q: 'Can you add AI features to a React Native app?',
                a: 'Yes. React Native applications can connect with AI services through secure APIs and backend systems. Common implementations include <strong>AI assistants, intelligent search, recommendations, document processing, image analysis, and workflow automation</strong>. For enterprise applications, we also account for data permissions, latency, model governance, security, monitoring, and operating costs.'
            }
        ]
    },
    {
        category: 'Hiring & Support',
        questions: [
            {
                q: 'How do I hire React Native developers for an existing product?',
                a: 'Look beyond basic React Native experience. A suitable developer should understand TypeScript and JavaScript, React Native architecture, Swift and Kotlin, REST and GraphQL APIs, state management, automated testing, performance optimization, and CI/CD and app release workflows. For an existing application, a codebase assessment is usually the best starting point. It helps identify technical debt, architecture constraints, and the skills your team actually needs.'
            },
            {
                q: 'Do you provide React Native maintenance and support after launch?',
                a: 'Yes. Support can cover <strong>framework upgrades</strong> (React Native and dependency updates), <strong>platform compatibility</strong> (new iOS and Android releases), <strong>performance and stability</strong> (crash monitoring, optimization, and defect resolution), <strong>security</strong> (dependency patches and security updates), and <strong>product evolution</strong> (new features, integrations, and release support).'
            }
        ]
    }
];

// Badges & Recognition: the app/software-development awards only — the default set leads with
// Web3, blockchain and AI. Order matters: the third card starts centred on desktop.
const RN_BADGE_ALTS = [
    'Techreviewer Top Software Developers',
    'RightFirms Software Development',
    'MobileAppDaily Top App Development Companies in Chicago',
    'ITRate Top Web Development Company',
    'SelectedFirms Top Healthcare Software Development Company'
];

export const rnAwardCards = AWARD_CARDS.filter((card) => RN_BADGE_ALTS.includes(card.alt)).sort(
    (a, b) => RN_BADGE_ALTS.indexOf(a.alt) - RN_BADGE_ALTS.indexOf(b.alt)
);

export const rnAwardsSubheading = 'Independent recognition for our app and software development work.';
